import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DemoModeService } from './demo-mode.service';
import { DemoStageService } from './demo-stage.service';
import { DEMO_FLOW_INTRO, DEMO_FLOW_START_ROUTE, DemoFlow, DemoIntro } from './demo.constants';
import { DemoContext } from './scripts/demo-script.types';
import { DEMO_SCRIPTS } from './scripts';

export type DemoPlayerStatus = 'idle' | 'playing' | 'done';

interface Point {
  x: number;
  y: number;
}

/** Throws to unwind a script that has been superseded (restart/exit/navigation). */
type AliveCheck = () => void;

const GLIDE_MS = 650; // keep in sync with the cursor CSS transition
const TYPE_CHAR_MS = 55;
const ZOOM_LEVEL = 1.4;
const ZOOM_MS = 430; // keep in sync with the <main> transition we set inline

/**
 * The animation engine. Builds a per-run {@link DemoContext} and runs a flow's
 * script end-to-end: gliding a virtual cursor onto `[data-demo]` anchors,
 * driving the page's reactive form, dispatching real clicks, and pausing for
 * beats — so the genuine app pages perform the task on their own on camera.
 *
 * Visual state (cursor position, ripple, highlight ring) is exposed as signals
 * consumed by {@link DemoOverlayComponent}. Each `play()` bumps a generation
 * token; a stale run throws at its next checkpoint and quietly stops.
 */
@Injectable({ providedIn: 'root' })
export class DemoPlayerService {
  private readonly router = inject(Router);
  private readonly demoMode = inject(DemoModeService);
  private readonly stage = inject(DemoStageService);

  private flow: DemoFlow | null = null;
  private generation = 0;
  private zoomedEl: HTMLElement | null = null;
  private zoomStartRect: DOMRect | null = null;
  private scrollLock: { el: HTMLElement; prev: string }[] = [];

  readonly cursor = signal<Point>({ x: window.innerWidth / 2, y: window.innerHeight - 120 });
  readonly cursorVisible = signal(false);
  readonly clicking = signal(false);
  readonly highlight = signal<DOMRect | null>(null);
  readonly spotTransform = signal('none');
  readonly spotOpacity = signal(0);
  readonly caption = signal('');
  readonly captionAnchor = signal<DOMRect | null>(null);
  readonly intro = signal<DemoIntro | null>(null);
  readonly status = signal<DemoPlayerStatus>('idle');
  readonly controlsHidden = signal(false);

  readonly isRunning = computed(() => this.status() === 'playing');

  /** Begin a flow from its start route. Supersedes any flow already playing. */
  async play(flow: DemoFlow): Promise<void> {
    const gen = ++this.generation;
    this.flow = flow;
    this.status.set('playing');
    this.clearFocus();
    this.cursorVisible.set(false);

    const alive: AliveCheck = () => {
      if (gen !== this.generation || !this.demoMode.active()) {
        throw new DemoCancelled();
      }
    };

    try {
      // Title card ("thumbnail") for a beat before the flow begins.
      this.intro.set(DEMO_FLOW_INTRO[flow]);
      await this.delay(2600);
      alive();
      this.intro.set(null);
      await this.delay(250);

      await DEMO_SCRIPTS[flow](this.makeContext(gen, alive));
    } catch (err) {
      if (!(err instanceof DemoCancelled)) {
        console.warn('[demo] script ended early:', err);
      }
      return;
    }

    if (gen !== this.generation) return;
    this.clearFocus();
    this.cursorVisible.set(false);
    this.controlsHidden.set(false); // bring controls back now the demo is finished
    this.status.set('done');
  }

  restart(): void {
    if (!this.flow) return;
    this.demoMode.activate(this.flow);
    void this.play(this.flow);
  }

  exit(): void {
    this.generation++;
    this.status.set('idle');
    this.cursorVisible.set(false);
    this.clearFocus();
    this.demoMode.deactivate();
    void this.router.navigateByUrl('/demo');
  }

  toggleControls(): void {
    this.controlsHidden.update((v) => !v);
  }

  // ---- context construction ---------------------------------------------

  private makeContext(gen: number, alive: AliveCheck): DemoContext {
    return {
      say: (text) => {
        this.caption.set(text);
      },
      goto: async (url) => {
        alive();
        this.cursorVisible.set(false);
        this.clearSpot();
        this.resetZoom();
        await this.router.navigateByUrl(url);
        this.setCaptionCentered(); // show the page-open caption while it loads
        await this.delay(950);
        this.clearCaption();
        alive();
      },
      moveTo: async (key) => {
        alive();
        await this.glideToElement(await this.waitForElement(key, alive), alive);
      },
      focus: async (key) => {
        alive();
        const el = await this.waitForElement(key, alive);
        await this.glideToElement(el, alive);
        await this.revealFocus(el, true); // spotlight only — just point it out
        alive();
        await this.delay(950);
        await this.endFocus();
      },
      click: async (key) => {
        alive();
        const el = await this.waitForElement(key, alive);
        await this.glideToElement(el, alive);
        await this.revealFocus(el, true); // buttons: spotlight only, never zoom
        alive();
        await this.delay(800); // hold so the spotlight + caption read on camera
        await this.ripple();
        // Retract focus BEFORE the real click, so any navigation happens at 1:1.
        await this.endFocus();
        alive();
        el.click();
        await this.delay(450);
      },
      type: async (key, controlPath, text) => {
        alive();
        const el = await this.waitForElement(key, alive);
        await this.glideToElement(el, alive);
        await this.revealFocus(el, true);
        this.focusInner(el);
        await this.delay(220);
        const control = this.requireControl(controlPath);
        let current = '';
        for (const ch of text) {
          alive();
          current += ch;
          control.setValue(current);
          control.markAsDirty();
          control.markAsTouched();
          control.updateValueAndValidity();
          await this.delay(TYPE_CHAR_MS);
        }
        await this.delay(550);
        await this.endFocus();
      },
      set: async (key, controlPath, value) => {
        alive();
        const el = await this.waitForElement(key, alive);
        await this.glideToElement(el, alive);
        await this.revealFocus(el, true);
        this.focusInner(el);
        await this.delay(420);
        alive();
        const control = this.requireControl(controlPath);
        control.setValue(value);
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
        await this.delay(750);
        await this.endFocus();
      },
      action: async (key, name, ...args) => {
        alive();
        if (key) {
          const el = await this.waitForElement(key, alive);
          await this.glideToElement(el, alive);
          await this.revealFocus(el, true);
          await this.delay(750);
          alive();
          this.stage.runAction(this.flowOrThrow(), name, args);
          await this.delay(550);
          await this.endFocus();
        } else {
          // No target (e.g. confirm send): show the caption centered briefly.
          this.setCaptionCentered();
          this.stage.runAction(this.flowOrThrow(), name, args);
          await this.delay(750);
          this.clearCaption();
        }
      },
      wait: async (ms) => {
        alive();
        await this.delay(ms);
      },
    };
  }

  // ---- internals --------------------------------------------------------

  private requireControl(path: string) {
    const control = this.stage.control(this.flowOrThrow(), path);
    if (!control) {
      throw new Error(`demo: control "${path}" not registered for flow "${this.flow}"`);
    }
    return control;
  }

  private async glideToElement(el: HTMLElement, alive: AliveCheck): Promise<void> {
    // Just fly the cursor in. The dim + spotlight border do NOT appear yet — they
    // are revealed together with the zoom in revealFocus(), so nothing has to
    // "re-calculate" or chase the zoom afterwards.
    await this.scrollIntoCenter(el, alive);
    alive();
    const rect = el.getBoundingClientRect();
    this.cursorVisible.set(true);
    this.cursor.set({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    await this.delay(GLIDE_MS);
    alive();
  }

  /** Center the element in the viewport and WAIT until the (smooth) scroll has
   * fully settled before returning — otherwise a later zoom would center on a
   * mid-scroll position and the target could end up off-screen (e.g. the Save
   * button at the bottom of a long form). */
  private async scrollIntoCenter(el: HTMLElement, alive: AliveCheck): Promise<void> {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    let lastTop = Number.NaN;
    for (let i = 0; i < 16; i++) {
      await this.delay(60);
      alive();
      const top = el.getBoundingClientRect().top;
      if (Math.abs(top - lastTop) < 0.5) return; // settled
      lastTop = top;
    }
  }

  // ---- spotlight + caption clearing -------------------------------------

  private clearSpot(): void {
    this.highlight.set(null);
    this.spotTransform.set('none');
    this.spotOpacity.set(0);
  }

  private clearFocus(): void {
    this.clearSpot();
    this.clearCaption();
    this.intro.set(null);
    this.resetZoom();
  }

  private setCaptionCentered(): void {
    this.captionAnchor.set(new DOMRect(window.innerWidth / 2, 84, 0, 0));
  }

  private clearCaption(): void {
    this.caption.set('');
    this.captionAnchor.set(null);
  }

  // ---- zoom -------------------------------------------------------------
  // We magnify the routed content (<main>) around the target and pan just
  // enough to keep the target fully on screen (so it never clips at an edge —
  // the bug with corner targets). The cursor/spotlight/tooltip overlay is a
  // sibling of <main>, so it stays crisp and aligns via live rects. Targets
  // under a position:fixed ancestor (modals) skip the zoom — the transform
  // would move them unpredictably — but still get the spotlight.

  private zoomMain(): HTMLElement | null {
    return document.querySelector('main');
  }

  private isZoomable(el: HTMLElement | null): boolean {
    const main = this.zoomMain();
    if (!el || !main || !main.contains(el)) return false;
    let node: HTMLElement | null = el;
    while (node && node !== main) {
      const pos = getComputedStyle(node).position;
      if (pos === 'fixed' || pos === 'sticky') return false;
      node = node.parentElement;
    }
    return true;
  }

  /** Reveal the dim + spotlight border AND start the zoom at the same instant.
   * The border carries the SAME transform as the page (one composited animation),
   * so it stays perfectly glued to the zoom with no per-frame work or repaint —
   * smooth, not chunky. The Turkish caption is anchored once to the final
   * position so it never wanders. Non-zoomable targets (modal cards) get the
   * spotlight without the magnify. */
  private async revealFocus(el: HTMLElement, noZoom = false): Promise<void> {
    const main = this.zoomMain();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const mL = 32, mR = 32, mT = 84, mB = 132; // keep clear of topbar & control bar

    // Only compact, non-button targets get magnified. Buttons (noZoom) and wide
    // fields stay at their real size with spotlight-only focus — scaling <main>
    // moves/enlarges them and was causing the Save button to scroll off / the
    // border to span edge-to-edge.
    let scale = 1;
    let r0: DOMRect | null = null;
    if (!noZoom && main && this.isZoomable(el)) {
      r0 = el.getBoundingClientRect();
      const maxW = (vw - mL - mR) / r0.width;
      const maxH = (vh - mT - mB) / r0.height;
      scale = Math.min(ZOOM_LEVEL, maxW, maxH);
    }

    const tooLarge = !!r0 && (r0.width > vw * 0.4 || r0.height > vh * 0.42);

    // Buttons, non-zoomable, too large, or no real magnification → spotlight only.
    if (noZoom || !r0 || tooLarge || scale < 1.12) {
      const rect = el.getBoundingClientRect();
      this.highlight.set(rect);
      this.spotTransform.set('none');
      this.captionAnchor.set(rect);
      this.spotOpacity.set(0);
      await this.nextFrame();
      this.spotOpacity.set(1);
      await this.delay(300);
      return;
    }

    const mb = main!.getBoundingClientRect(); // measured at scale 1
    const cx = r0.left + r0.width / 2;
    const cy = r0.top + r0.height / 2;
    const s = scale;
    const sw = r0.width * s;
    const sh = r0.height * s;
    const tx = this.clampPan(cx, sw, mL, vw - mR, vw);
    const ty = this.clampPan(cy, sh, mT, vh - mB, vh);
    const transform = `translate(${tx}px, ${ty}px) scale(${s})`;

    this.zoomedEl = el;
    this.zoomStartRect = r0;
    // The scaled <main> is wider/taller than the viewport; clip the overflow so
    // the browser doesn't grow the scrollable area (the x-axis overflow bug).
    this.lockScroll();
    // Anchor the caption once, to where the target ends up — stable for the step.
    this.captionAnchor.set(new DOMRect(cx + tx - sw / 2, cy + ty - sh / 2, sw, sh));
    // Frame 1: border sits exactly on the un-zoomed target, transparent.
    this.highlight.set(r0);
    this.spotTransform.set('none');
    this.spotOpacity.set(0);
    await this.nextFrame();

    // Frame 2: page <main> and the border get the IDENTICAL transform + the dim
    // fades in — all on the same frame, same easing, fully in sync.
    main!.style.transformOrigin = `${cx - mb.left}px ${cy - mb.top}px`;
    main!.style.transition = `transform ${ZOOM_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    main!.style.transform = transform;
    this.spotTransform.set(transform); // border box is centered on the target, origin 50% 50%
    this.spotOpacity.set(1);
    this.cursor.set({ x: cx + tx, y: cy + ty });
    await this.delay(ZOOM_MS + 60);
  }

  /** Retract the zoom (page + border together), fade the dim out, clear caption. */
  private async endFocus(): Promise<void> {
    const main = this.zoomMain();
    this.spotOpacity.set(0);
    this.clearCaption();

    if (this.zoomedEl && main && main.style.transform && main.style.transform !== 'none') {
      main.style.transform = 'none';
      this.spotTransform.set('none');
      if (this.zoomStartRect) {
        this.cursor.set({
          x: this.zoomStartRect.left + this.zoomStartRect.width / 2,
          y: this.zoomStartRect.top + this.zoomStartRect.height / 2,
        });
      }
      await this.delay(ZOOM_MS + 60);
    } else {
      await this.delay(260);
    }

    this.zoomedEl = null;
    this.zoomStartRect = null;
    this.unlockScroll();
    this.clearSpot();
  }

  /** Clip horizontal/vertical overflow while a zoom is active, so the scaled
   * <main> (wider than the viewport) doesn't add a scrollbar / x-axis overflow.
   * Uses `overflow: clip` (no scroll container, no scrollbar-gutter shift). */
  private lockScroll(): void {
    if (this.scrollLock.length) return;
    const els = [document.documentElement, document.body].filter(Boolean) as HTMLElement[];
    for (const el of els) {
      this.scrollLock.push({ el, prev: el.style.overflow });
      el.style.overflow = 'clip';
    }
  }

  private unlockScroll(): void {
    for (const { el, prev } of this.scrollLock) {
      el.style.overflow = prev;
    }
    this.scrollLock = [];
  }

  /** How far to shift a magnified span (centered at `center`, length `size`)
   * so it sits within [min, max]; centers it if it's larger than the window. */
  private clampPan(center: number, size: number, min: number, max: number, full: number): number {
    const start = center - size / 2;
    const end = center + size / 2;
    if (size > max - min) return full / 2 - center;
    if (start < min) return min - start;
    if (end > max) return max - end;
    return 0;
  }

  private resetZoom(): void {
    const main = this.zoomMain();
    if (main) {
      main.style.transition = '';
      main.style.transform = '';
      main.style.transformOrigin = '';
    }
    this.zoomedEl = null;
    this.zoomStartRect = null;
    this.unlockScroll();
    this.spotOpacity.set(0);
  }

  private nextFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private async ripple(): Promise<void> {
    this.clicking.set(true);
    await this.delay(260);
    this.clicking.set(false);
    await this.delay(80);
  }

  private focusInner(host: HTMLElement): void {
    const focusable = host.matches('input, textarea')
      ? host
      : (host.querySelector('input, textarea, [contenteditable="true"]') as HTMLElement | null);
    focusable?.focus({ preventScroll: true });
  }

  private async waitForElement(demoKey: string, alive: AliveCheck, timeoutMs = 9000): Promise<HTMLElement> {
    const selector = `[data-demo="${demoKey}"]`;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      alive();
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) return el;
      }
      await this.delay(120);
    }
    throw new Error(`demo: timed out waiting for [data-demo="${demoKey}"]`);
  }

  private flowOrThrow(): DemoFlow {
    if (!this.flow) throw new Error('demo: no active flow');
    return this.flow;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

class DemoCancelled extends Error {
  constructor() {
    super('demo: cancelled');
  }
}

export { DEMO_FLOW_START_ROUTE };
